import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { CheckCircle, EventAvailable, EventBusy } from "@mui/icons-material";
import { checkinService, CheckinInfo } from "../services/dayScheduleService";
import { colors } from "../theme/designSystem";

const REASON_MAX = 300;

/**
 * Check-in público (RN-547): o paciente abre o link do WhatsApp, vê a sessão e confirma presença.
 * Com `acao=cancelar` (RN-547a) o mesmo token abre o cancelamento: "Não poderei comparecer", uma
 * segunda confirmação e motivo opcional. Sem login: alias + token na URL. Nunca mostra dados além do
 * necessário para reconhecer a sessão.
 */
const Checkin: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const alias = params.get("clinic") || "";
  const token = params.get("t") || "";
  const cancelMode = params.get("acao") === "cancelar";

  const [info, setInfo] = useState<CheckinInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [askingCancel, setAskingCancel] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    document.title = cancelMode ? "Cancelamento de sessão" : "Confirmação de presença";
    if (!alias || !token) { setLoading(false); return; }
    checkinService.get(alias, token)
      .then(setInfo)
      .catch(() => setError("Não foi possível abrir o link. Tente de novo em instantes."))
      .finally(() => setLoading(false));
  }, [alias, token, cancelMode]);

  const run = async (action: () => Promise<CheckinInfo>, fallback: string) => {
    setBusy(true);
    setError("");
    try {
      setInfo(await action());
      setAskingCancel(false);
    } catch (e) {
      const d = (e as { response?: { data?: { description?: string } } })?.response?.data;
      setError(d?.description || fallback);
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => run(() => checkinService.confirm(alias, token), "Não foi possível confirmar. Fale com a clínica.");
  const cancel = () => run(() => checkinService.cancel(alias, token, reason), "Não foi possível cancelar. Fale com a clínica.");

  const card = (children: React.ReactNode) => (
    <Box sx={{ minHeight: "100vh", background: colors.background, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 440, background: "#fff", borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.10)", p: { xs: 3, sm: 4 }, textAlign: "center" }}>
        {children}
      </Box>
    </Box>
  );

  if (loading) return card(<CircularProgress sx={{ color: colors.primary }} />);

  if (!alias || !token || !info || info.status === "invalid") {
    return card(<>
      <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, mb: 1 }}>Link inválido</Typography>
      <Typography sx={{ color: colors.textSecondary }}>Este link não é válido. Fale com a clínica.</Typography>
    </>);
  }

  if (info.status === "gone") {
    return card(<>
      <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, mb: 1 }}>Sessão não encontrada</Typography>
      <Typography sx={{ color: colors.textSecondary }}>Este agendamento não está mais ativo. Fale com {info.clinicName || "a clínica"}.</Typography>
    </>);
  }

  const cancelled = info.status === "cancelled";
  const confirmed = info.status === "confirmed";
  const expired = info.status === "expired";
  // No modo cancelar, "tarde demais" vale tanto para link vencido quanto para sessão já iniciada
  const tooLate = cancelMode && !cancelled && !info.canCancel;

  const icon = cancelled ? <EventBusy sx={{ fontSize: 56 }} />
    : !cancelMode && confirmed ? <CheckCircle sx={{ fontSize: 56 }} />
    : cancelMode ? <EventBusy sx={{ fontSize: 56 }} />
    : <EventAvailable sx={{ fontSize: 56 }} />;
  const iconColor = cancelled || (cancelMode && !tooLate) ? colors.error : confirmed ? colors.success : colors.primary;
  const greeting = `Olá${info.patientFirstName ? `, ${info.patientFirstName}` : ""}!`;

  const title = cancelled ? "Sessão cancelada"
    : tooLate ? "Não é mais possível cancelar"
    : cancelMode ? (askingCancel ? "Confirmar o cancelamento?" : greeting)
    : confirmed ? "Presença confirmada"
    : expired ? "Link vencido"
    : greeting;

  const subtitle = cancelled ? (info.cancelledAt ? `Cancelada em ${info.cancelledAt}. Para remarcar, fale com a clínica.` : "Esta sessão foi cancelada. Para remarcar, fale com a clínica.")
    : tooLate ? "A sessão já começou. Se precisar, fale com a clínica."
    : cancelMode ? (askingCancel ? "A clínica será avisada. Se quiser, conte o motivo." : "Não vai poder comparecer? Você pode cancelar a sessão abaixo.")
    : confirmed ? `Confirmado em ${info.confirmedAt}. Até lá!`
    : expired ? "Este link de confirmação venceu. Se precisar, fale com a clínica."
    : "Confirme sua presença na sessão abaixo.";

  const buttonSx = { textTransform: "none", fontWeight: 700, fontSize: "1rem", py: 1.25, boxShadow: "none", borderRadius: "10px" } as const;

  return card(<>
    <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, letterSpacing: ".06em", textTransform: "uppercase", mb: 1 }}>{info.clinicName}</Typography>
    <Box sx={{ color: iconColor, mb: 1 }}>{icon}</Box>
    <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ color: colors.textSecondary, mb: 2.5 }}>{subtitle}</Typography>

    <Box sx={{ textAlign: "left", background: colors.backgroundSubtle, border: `1px solid ${colors.borderLight}`, borderRadius: "12px", p: 2, mb: 2.5, display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", fontSize: "0.95rem" }}>
      <Box sx={{ color: colors.textSecondary }}>Data</Box><Box sx={{ fontWeight: 600 }}>{info.date} <span style={{ color: colors.textSecondary, fontWeight: 400 }}>({info.weekday})</span></Box>
      <Box sx={{ color: colors.textSecondary }}>Horário</Box><Box sx={{ fontWeight: 600 }}>{info.start} às {info.end}</Box>
      {info.professionalName && <><Box sx={{ color: colors.textSecondary }}>Profissional</Box><Box sx={{ fontWeight: 600 }}>{info.professionalName}{info.specialty ? ` · ${info.specialty}` : ""}</Box></>}
    </Box>

    {error && <Typography sx={{ color: colors.error, mb: 2, fontSize: "0.9rem" }}>{error}</Typography>}

    {!cancelMode && !confirmed && !expired && !cancelled && (
      <Button onClick={confirm} variant="contained" disabled={busy} fullWidth size="large" startIcon={busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <CheckCircle />}
        sx={{ ...buttonSx, bgcolor: colors.success, "&:hover": { bgcolor: colors.successHover } }}>
        Confirmar presença
      </Button>
    )}

    {cancelMode && !cancelled && !tooLate && !askingCancel && (
      <Button onClick={() => setAskingCancel(true)} variant="outlined" fullWidth size="large" startIcon={<EventBusy />}
        sx={{ ...buttonSx, borderColor: colors.error, color: colors.error, "&:hover": { borderColor: colors.errorHover, bgcolor: colors.errorSoft } }}>
        Não poderei comparecer
      </Button>
    )}

    {cancelMode && !cancelled && !tooLate && askingCancel && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField label="Motivo (opcional)" value={reason} onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))} multiline minRows={2} size="small" fullWidth
          helperText={`${reason.length}/${REASON_MAX}`} FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }} />
        <Button onClick={cancel} variant="contained" disabled={busy} fullWidth size="large" startIcon={busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <EventBusy />}
          sx={{ ...buttonSx, bgcolor: colors.error, "&:hover": { bgcolor: colors.errorHover } }}>
          Cancelar a sessão
        </Button>
        <Button onClick={() => setAskingCancel(false)} disabled={busy} fullWidth sx={{ textTransform: "none", color: colors.textSecondary }}>Voltar</Button>
      </Box>
    )}

    <Typography sx={{ fontSize: "0.75rem", color: colors.textMuted, mt: 2 }}>Precisa remarcar? Responda a mensagem no WhatsApp da clínica.</Typography>
  </>);
};

export default Checkin;
